<template>
  <div v-if="deck" class="umdb-deck-view">
    <h1>
      {{ deck.name }} <span class="small text-muted"> from {{ deck.set }}</span>
    </h1>
    <b-row class="mb-3">
      <b-col sm>
        <!-- <h2>{{ totalHeroes > 1 ? 'Heroes' : 'Hero'}}</h2> -->
        <div v-for="fighter in deck.heroes" :key="fighter.name">
          <h3>
            {{ fighter.name }}
            <small v-if="fighter.quantity > 1" class="text-muted"
              >&times;{{ fighter.quantity }}</small
            >
          </h3>
          <span class="mr-4">
            <img
              v-if="fighter.attack_type === 'melee'"
              src="@/assets/images/melee.svg"
              :style="{ width: '8em' }"
            />
            <img
              v-else-if="fighter.attack_type === 'ranged'"
              src="@/assets/images/ranged.svg"
              :style="{ width: '8em' }"
            />
            <span v-else>{{ fighter.attack_type }}</span>
          </span>
          <span class="text-nowrap"
            ><strong>Start health</strong> {{ fighter.hp }}</span
          >
        </div>
      </b-col>
      <b-col sm v-if="totalSidekicks">
        <!-- <h2>{{ totalSidekicks > 1 ? 'Sidekicks' : 'Sidekick'}}</h2> -->
        <div v-for="fighter in deck.sidekicks" :key="fighter.name">
          <h3>
            {{ fighter.name }}
            <small v-if="fighter.quantity > 1" class="text-muted"
              >&times;{{ fighter.quantity }}</small
            >
          </h3>
          <span class="mr-4">
            <img
              v-if="fighter.attack_type === 'melee'"
              src="@/assets/images/melee.svg"
              :style="{ width: '8em' }"
            />
            <img
              v-else-if="fighter.attack_type === 'ranged'"
              src="@/assets/images/ranged.svg"
              :style="{ width: '8em' }"
            />
            <span v-else>{{ fighter.attack_type }}</span>
          </span>
          <span class="text-nowrap"
            ><strong>Start health</strong> {{ fighter.hp }}</span
          >
        </div>
      </b-col>
    </b-row>

    <h4 class="mb-3">
      <span>Movement </span>
      <span v-if="deck.movement">{{ deck.movement }}</span>
      <span v-else class="small text-muted">Unknown</span>
    </h4>
    <div>
      <h4>Special abilities</h4>
      <p v-html="parseMarkdown(deck.special ? deck.special : 'Unknown')">
        {{ deck.special ? deck.special : "Unknown" }}
      </p>
    </div>

    <div v-if="deck.notes">
      <h4>Notes</h4>
      <p v-html="parseMarkdown(deck.notes)">
        {{ deck.notes }}
      </p>
    </div>

    <div v-if="deck.quote">
      <h4>Quote</h4>
      <p v-html="parseMarkdown(deck.quote)">
        {{ deck.quote }}
      </p>
    </div>

    <b-button
      v-if="deck.cards.length"
      :to="{ name: 'CardCounter', params: { slug } }"
      >Count cards</b-button
    >

    <div v-if="deck.cards.length">
      <h4>Deck stats</h4>
      <DeckAnalysis :cards="deck.cards" />
    </div>

    <div v-if="deck.cards.length">
      <h4>Cards</h4>
      <b-table
        :items="items"
        :fields="fields"
        sticky-header="85vh"
        no-border-collapse
        class="compact-table-sm"
      >
        <template v-slot:cell(quantity)="quantity">
          {{ quantity.value }}&times;
        </template>

        <template v-slot:cell(title)="title">
          <router-link
            :to="{ name: 'UmDbCard', params: { slug: title.item.slug } }"
          >
            <div>{{ title.value }}</div>
            <b-img-lazy
              v-if="title.item.image"
              :src="title.item.image"
              width="100"
              height="140"
            />
          </router-link>
        </template>

        <template v-slot:cell(type)="type">
          <img
            v-if="type.value === 'attack'"
            src="@/assets/images/attack.svg"
          />
          <img
            v-else-if="type.value === 'defence' || type.value === 'defense'"
            src="@/assets/images/defence.svg"
          />
          <img
            v-else-if="type.value === 'versatile'"
            src="@/assets/images/versatile.svg"
          />
          <img
            v-else-if="type.value === 'scheme'"
            src="@/assets/images/scheme.svg"
          />
        </template>

        <template v-slot:cell(other_decks)="data">
          <span
            tabindex="0"
            :id="$id(data.item.slug)"
            :class="[data.value ? 'info-tooltip' : '']"
            >{{ data.value }}</span
          >
          <b-popover
            v-if="data.value"
            :key="'popover-' + data.item.slug"
            :target="$id(data.item.slug)"
            triggers="hover focus"
            placement="auto"
            title="Also found in:"
          >
            <p
              v-for="other_deck in data.item.other_decks"
              :key="other_deck.slug"
            >
              <router-link
                :to="{ name: 'UmDbDeck', params: { slug: other_deck.slug } }"
              >
                {{ other_deck.name }}
              </router-link>
            </p>
          </b-popover>
        </template>

        <template v-slot:row-details="card">
          <b-row class="mx-sm-1 mx-md-5">
            <b-col>
              <p v-if="card.item.basicText">
                {{ card.item.basicText }}
              </p>
              <p v-if="card.item.type !== 'scheme' && card.item.immediateText">
                <strong>Immediately:</strong> {{ card.item.immediateText }}
              </p>
              <p v-if="card.item.type !== 'scheme' && card.item.duringText">
                <strong>During combat:</strong> {{ card.item.duringText }}
              </p>
              <p v-if="card.item.type !== 'scheme' && card.item.afterText">
                <strong>After combat:</strong> {{ card.item.afterText }}
              </p>
            </b-col>
          </b-row>
          <b-row
            v-if="card.item.card_notes || card.item.deck_card_notes"
            class="mx-sm-1 mx-md-5"
          >
            <b-col>
              <b-card header="Notes">
                <b-card-text
                  ><p
                    v-if="card.item.card_notes"
                    v-html="parseMarkdown(card.item.card_notes)"
                  ></p>
                  <hr
                    v-if="card.item.card_notes && card.item.deck_card_notes" />
                  <p
                    v-if="card.item.deck_card_notes"
                    v-html="parseMarkdown(card.item.deck_card_notes)"
                  ></p
                ></b-card-text>
              </b-card>
            </b-col>
          </b-row>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import DeckAnalysis from "@/components/DeckAnalysis.vue";
import api from "@/api";
import markdown from "@/mixins/markdown";

export default {
  name: "UmDbDeck",
  mixins: [markdown],
  metaInfo() {
    const title = this.deck ? `${this.deck.name} | Decks | UmDb` : undefined;
    return {
      title,
    };
  },
  components: { DeckAnalysis },
  data() {
    return {
      deck: null,
      fields: [
        { key: "quantity", label: "#", sortable: true },
        { key: "title", sortable: true },
        { key: "characterName", sortable: true },
        {
          key: "type",
          sortable: true,
          tdClass: (value) => [
            value,
            "card-type",
            "text-right",
            "align-middle",
          ],
          thClass: "vertical",
        },
        {
          key: "value",
          tdClass: (value, key, item) => [
            item.type,
            "card-value",
            "align-middle",
          ],
          thClass: "vertical",
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) =>
            item.type === "scheme" ? null : value,
        },
        {
          key: "boost",
          tdClass: ["text-right"],
          thClass: "vertical",
          sortable: true,
        },
        {
          key: "other_decks",
          tdClass: ["text-right"],
          thClass: "vertical",
          sortable: true,
          sortByFormatted: true,
          formatter: (value) => value.length,
        },
      ],
    };
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  computed: {
    totalHeroes() {
      return this.deck.heroes.reduce(
        (acc, fighter) => acc + fighter.quantity,
        0
      );
    },
    totalSidekicks() {
      return this.deck.sidekicks.reduce(
        (acc, fighter) => acc + fighter.quantity,
        0
      );
    },
    items() {
      return this.deck.cards.map((card) => ({ ...card, _showDetails: true }));
    },
  },
  methods: {
    getDeck(slug) {
      api
        .getDBDeck(slug)
        .then((data) => {
          this.deck = data;
        })
        .catch(() => {
          this.$router.replace({ name: "Error" });
        });
    },
  },
  mounted() {
    this.getDeck(this.slug);
  },
  beforeRouteUpdate(to, from, next) {
    this.getDeck(to.params.slug);
    next();
  },
};
</script>

<style lang="less">
@import "~@/assets/less/mixins";

.umdb-deck-view {
  .card-type {
    border-radius: 0.5rem 0 0 0.5rem;
    padding-right: 0;
  }
  .card-value {
    font-size: 1.5rem;
    font-weight: bold;
    border-radius: 0 0.5rem 0.5rem 0;
  }
  .attack {
    background-color: @attack-red !important;
    color: white;
    img {
      width: 2.1em;
    }
  }

  .defense,
  .defence {
    background-color: @defence-blue !important;
    color: white;
    img {
      width: 1.65em;
    }
  }

  .versatile {
    background-color: @versatile-violet !important;
    color: white;
    img {
      width: 2em;
    }
  }

  .scheme {
    background-color: @scheme-yellow !important;
    color: white;
    img {
      height: 2em;
      margin-right: -0.5em;
    }
  }
}
</style>
